// STRIKZA — The Exchange
//
// Faithfully matches the prototype design at
// `UI Refactor/Build/exchange.{jsx,css}`. Every visible value comes
// from the curated public-ready signal-card surface (Supabase RLS-
// gated `signal_cards` via `usePublicSignalCards`) plus the user's
// own watchlist. NO hardcoded fixtures, NO invented agent debate,
// NO fabricated edge / probability / consensus values.
//
// Hooks consumed:
//   - usePublicSignalCards (paginated, read-only)
//   - usePublicSignalCardWatchlist (saved-card set + toggle)
//   - useSignalCalibrationSummary (drawer)
//   - useSignalTimeline (drawer)
//
// Doctrine carry-forward:
//   - Manual placement only.
//   - Observation-only board. No bookmaker connection. No execution.

import { useMemo, useState } from "react";

import { Icon } from "../../components/icons/Icon.tsx";
import { MarketFilter } from "../../components/MarketFilter.tsx";
import { filterByMarket, type MarketFilterMode } from "../../agents/value-edge.ts";
import { usePublicSignalCards } from "../../exchange/usePublicSignalCards.ts";
import { useProgressiveReveal } from "../../exchange/useProgressiveReveal.ts";
import { usePublicSignalCardWatchlist } from "../../exchange/usePublicSignalCardWatchlist.ts";
import type { PublicSignalCardViewModel } from "../../exchange/usePublicSignalCards.ts";
import { PublicSignalCardItem } from "../../exchange/PublicSignalCardItem.tsx";
import { ExchangeStats } from "./ExchangeStats.tsx";
import { ExchangeToolbar } from "./ExchangeToolbar.tsx";
import { ExchangeDiscoveryTabs } from "./ExchangeDiscoveryTabs.tsx";
import { ExchangeChipRail } from "./ExchangeChipRail.tsx";
import { ExchangeSignalRow } from "./ExchangeSignalRow.tsx";
import { ExchangeDetailDrawer } from "./ExchangeDetailDrawer.tsx";
import {
  computeExchangeStats,
  countByChip,
  countByTab,
  filterByChip,
  filterByTab,
  sortCards,
  type ChipId,
  type DiscoveryTabId,
  type SortKey,
} from "./exchange-derive.ts";

export function ExchangePage(): JSX.Element {
  // Reveal the entire board (substrate can scale to 1000+ priced selections).
  const result = usePublicSignalCards({ all: true });
  const watchlist = usePublicSignalCardWatchlist();

  const [activeTab, setActiveTab] = useState<DiscoveryTabId>("all");
  const [activeChip, setActiveChip] = useState<ChipId>("all");
  const [activeMarket, setActiveMarket] = useState<MarketFilterMode>("all");
  const [search, setSearch] = useState<string>("");
  const [sort, setSort] = useState<SortKey>("edge");
  const [view, setView] = useState<"table" | "cards">("table");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const now = useMemo(() => new Date(), []);
  const watchedIds = watchlist.savedSignalCardIds;

  const stats = useMemo(
    () => computeExchangeStats(result.cards, watchedIds, now),
    [result.cards, watchedIds, now],
  );

  const tabCounts = useMemo<Record<DiscoveryTabId, number>>(
    () => ({
      all: countByTab(result.cards, "all", watchedIds, now),
      trending: countByTab(result.cards, "trending", watchedIds, now),
      "high-edge": countByTab(result.cards, "high-edge", watchedIds, now),
      ending: countByTab(result.cards, "ending", watchedIds, now),
      watch: countByTab(result.cards, "watch", watchedIds, now),
      disagree: countByTab(result.cards, "disagree", watchedIds, now),
    }),
    [result.cards, watchedIds, now],
  );

  // Apply filters in order: tab → chip → market → search.
  const filtered = useMemo(() => {
    let out = filterByTab(result.cards, activeTab, watchedIds, now);
    out = filterByChip(out, activeChip, now);
    out = [
      ...filterByMarket(out, activeMarket, (c) => c.signal_label ?? c.value_label ?? null),
    ];
    if (search.trim().length > 0) {
      const q = search.trim().toLowerCase();
      out = out.filter((c) => {
        const haystack = [
          c.signal_label ?? "",
          c.canonical_code,
          c.id,
          c.value_label ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return sortCards(out, sort);
  }, [result.cards, activeTab, activeChip, activeMarket, search, sort, watchedIds, now]);

  // Bounded initial render — reveal more as the user scrolls (handles a
  // substrate-scale 1000+ card board without choking first paint).
  const reveal = useProgressiveReveal(filtered.length);
  const visibleCards = useMemo(
    () => filtered.slice(0, reveal.visible),
    [filtered, reveal.visible],
  );

  const chipCounts = useMemo<Record<ChipId, number>>(() => {
    const tabFiltered = filterByTab(result.cards, activeTab, watchedIds, now);
    return {
      all: tabFiltered.length,
      tier1: countByChip(tabFiltered, "tier1", now),
      ghana: countByChip(tabFiltered, "ghana", now),
      btts: countByChip(tabFiltered, "btts", now),
      overunder: countByChip(tabFiltered, "overunder", now),
      dc: countByChip(tabFiltered, "dc", now),
      ah: countByChip(tabFiltered, "ah", now),
      today: countByChip(tabFiltered, "today", now),
      weekend: countByChip(tabFiltered, "weekend", now),
    };
  }, [result.cards, activeTab, watchedIds, now]);

  const selected: PublicSignalCardViewModel | null = useMemo(() => {
    if (selectedId === null) return null;
    return result.cards.find((c) => c.id === selectedId) ?? null;
  }, [selectedId, result.cards]);

  const filterCount = (activeChip !== "all" ? 1 : 0) + (search.trim().length > 0 ? 1 : 0);

  return (
    <>
      <div className="ex-head">
        <div>
          <h1>The Exchange</h1>
          <div className="sub">
            Football market intelligence · {String(stats.liveCount)} live signals across the
            curated public-ready surface
          </div>
        </div>
        <div className="pill-row">
          <span className="tag phantom">
            <span className="dot" />
            Phantom
          </span>
          <span className="tag safe">
            <span className="dot" />
            Safe Mode
          </span>
          <span className="tag">Phase 1A</span>
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          Observation-only board. Signals are deterministic estimates — not bookmaker offers, not
          tradable inside Strikza. You always place manually on your platform of choice.
        </span>
      </div>

      <ExchangeStats stats={stats} loading={result.loading} />

      <ExchangeToolbar
        search={search}
        onSearch={setSearch}
        filterCount={filterCount}
        sort={sort}
        onSort={setSort}
        view={view}
        onViewChange={setView}
      />

      <ExchangeDiscoveryTabs active={activeTab} onChange={setActiveTab} counts={tabCounts} />

      <ExchangeChipRail active={activeChip} onChange={setActiveChip} counts={chipCounts} />

      <MarketFilter
        value={activeMarket}
        onChange={setActiveMarket}
      />

      {result.error !== null ? (
        <div className="ex-table">
          <div style={{ padding: 22, color: "var(--text-tertiary)" }}>
            Curated signal cards unavailable: {result.error}
          </div>
        </div>
      ) : view === "table" ? (
        <div className="ex-table">
          <div className="ex-thead">
            <span />
            <span>Fixture</span>
            <span>Selection</span>
            <span className="sortable on">
              Probability <Icon name="chev" size={10} />
            </span>
            <span className="sortable on">Edge ↓</span>
            <span>Conf.</span>
            <span>Window</span>
            <span />
          </div>
          {result.loading ? (
            <div style={{ padding: 22, color: "var(--text-tertiary)" }}>
              Loading curated signal cards…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 22, color: "var(--text-tertiary)" }}>
              No signal cards match the current filters. Adjust tabs, chips, or search.
            </div>
          ) : (
            <>
              {visibleCards.map((c) => (
                <div key={c.id}>
                  <ExchangeSignalRow
                    card={c}
                    isWatched={watchlist.isSaved(c.id)}
                    saveBusy={watchlist.togglingSignalCardId === c.id}
                    onToggleWatch={(id) => {
                      void watchlist.toggleSignalCard(id);
                    }}
                    onOpen={(card) => setSelectedId(selectedId === card.id ? null : card.id)}
                    now={now}
                  />
                  {selected !== null && selected.id === c.id ? (
                    <ExchangeDetailDrawer
                      card={selected}
                      isWatched={watchlist.isSaved(selected.id)}
                      onToggleWatch={(id) => {
                        void watchlist.toggleSignalCard(id);
                      }}
                    />
                  ) : null}
                </div>
              ))}
              {reveal.hasMore ? (
                <div
                  ref={reveal.sentinelRef}
                  style={{ padding: 14, textAlign: "center", color: "var(--text-tertiary)" }}
                >
                  Showing {visibleCards.length} of {filtered.length} — scroll for more…
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 16,
            }}
          >
            {visibleCards.map((c) => (
              <PublicSignalCardItem
                key={c.id}
                card={c}
                onInspect={(picked) => setSelectedId(picked.id)}
                isSaved={watchlist.isSaved(c.id)}
                saveBusy={watchlist.togglingSignalCardId === c.id}
                onToggleSave={(picked) => {
                  void watchlist.toggleSignalCard(picked.id);
                }}
              />
            ))}
          </div>
          {reveal.hasMore ? (
            <div
              ref={reveal.sentinelRef}
              style={{ padding: 14, textAlign: "center", color: "var(--text-tertiary)" }}
            >
              Showing {visibleCards.length} of {filtered.length} — scroll for more…
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
